import { memo, useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  Chip,
  Divider,
  Fab,
  IconButton,
  Link as MuiLink,
  Button,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { Add, ExpandLess, ExpandMore, PhotoOutlined, Remove, Search } from '@mui/icons-material'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import './ImpresCatalog.css'
import productSearchEmptyState from '../TdSynnex/product-search-empty-state.png'
import { useImpresCatalogSearch } from './useImpresCatalogSearch'
import { EPEAT_LABELS, ImpresCatalogItem, ImpresSupplierOffer } from './impresTypes'
import ImpresCatalogFilters, { EMPTY_FILTER_SELECTION, itemMatchesFilters } from './ImpresCatalogFilters'
import { ImpresCartLine, useImpresCart } from './ImpresCartContext'
import { formatCurrency } from '../../helper'
import SupplierPartDrawer from '../SupplierPartDrawer'
import { SIDEBAR_SUPPLIERS, SidebarSupplier, useSupplierPartLookup } from '../useSupplierPartLookup'

const dash = '—'
const text = (v: string | null | undefined) => v || dash
const currency = (v: number | null | undefined) => (v === null || v === undefined ? dash : formatCurrency(v))

const ImpresPartCard = memo(function ImpresPartCard({
  item,
  cartForItem,
  setQty,
  onOpenSupplierPart,
}: {
  item: ImpresCatalogItem
  cartForItem: Partial<Record<ImpresSupplierOffer['supplierName'], ImpresCartLine>> | undefined
  setQty: (item: ImpresCatalogItem, supplierName: ImpresSupplierOffer['supplierName'], qty: number) => void
  onOpenSupplierPart: (supplier: SidebarSupplier, partNumber: string, mfgPN: string) => void
}) {
  const [expanded, setExpanded] = useState(true)

  const epeatLabel = item.epeat && item.epeat !== 'N' ? EPEAT_LABELS[item.epeat] ?? item.epeat : null
  const pricedSupplierCount = item.suppliers.filter(s => s.unitPrice !== null).length
  const lowestPrice =
    pricedSupplierCount < 2
      ? null
      : item.suppliers.reduce<number | null>((min, s) => {
          if (s.unitPrice === null) return min
          return min === null || s.unitPrice < min ? s.unitPrice : min
        }, null)

  return (
    <Card>
      <div className="impres-card-header">
        <div className="impres-card-header-content">
          <Typography variant="h5">{item.mfgPN}</Typography>
          {item.taa === 'C' && <Chip label="TAA Compliant" color="success" size="small" />}
          {epeatLabel && <Chip label={`EPEAT ${epeatLabel}`} size="small" />}
          {item.energyStar === 'C' && <Chip label="Energy Star" size="small" />}
        </div>
        <IconButton onClick={() => setExpanded(v => !v)} aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>
      {expanded && (
        <>
          <Divider />
          <CardContent className="impres-card-content">
            <div className="impres-card-top">
              <div className="impres-card-media">
                <PhotoOutlined fontSize="large" />
              </div>
              <div className="impres-card-description">
                <Typography variant="caption" className="impres-info-label">
                  Description
                </Typography>
                <Typography>{text(item.description)}</Typography>
              </div>
            </div>
            <Divider />
            <div className="impres-info-row">
              <div className="impres-info-cell">
                <Typography variant="caption" className="impres-info-label">
                  Manufacturer
                </Typography>
                <Typography>{text(item.manufacturer)}</Typography>
              </div>
              <Divider orientation="vertical" flexItem />
              <div className="impres-info-cell">
                <Typography variant="caption" className="impres-info-label">
                  MSRP
                </Typography>
                <Typography>{currency(item.msrp)}</Typography>
              </div>
              <Divider orientation="vertical" flexItem />
              <div className="impres-info-cell">
                <Typography variant="caption" className="impres-info-label">
                  Product Category
                </Typography>
                <Typography>{text(item.productCategory)}</Typography>
              </div>
              <Divider orientation="vertical" flexItem />
              <div className="impres-info-cell">
                <Typography variant="caption" className="impres-info-label">
                  Country of Origin
                </Typography>
                <Typography>{text(item.countryOfOrigin)}</Typography>
              </div>
            </div>
          </CardContent>
          <Divider />
          <Typography variant="h6" className="impres-suppliers-heading">
            Suppliers
          </Typography>
          <Divider />
          {item.suppliers.map((supplier, index) => {
            const qty = cartForItem?.[supplier.supplierName]?.qty ?? 0
            const isDell = supplier.supplierName === 'Dell'
            return (
              <div key={supplier.supplierName}>
                <div className="impres-supplier-row">
                  <div className="impres-supplier-row-left">
                    <div className="impres-supplier-row-name">
                      {supplier.catalogPageUrl ? (
                        <MuiLink component={RouterLink} to={supplier.catalogPageUrl}>
                          {supplier.supplierName}
                        </MuiLink>
                      ) : (
                        <Typography>{supplier.supplierName}</Typography>
                      )}
                      {supplier.unitPrice !== null && supplier.unitPrice === lowestPrice && (
                        <Chip label="Lowest Price" color="success" size="small" />
                      )}
                    </div>
                    <Typography variant="body2" className="impres-info-label">
                      Supplier part:{' '}
                      {isDell && supplier.supplierPartNumber ? (
                        <MuiLink
                          component={RouterLink}
                          to={`/imprescatalogs/dell?pn=${encodeURIComponent(supplier.supplierPartNumber)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {supplier.supplierPartNumber}
                        </MuiLink>
                      ) : SIDEBAR_SUPPLIERS.has(supplier.supplierName) && supplier.supplierPartNumber ? (
                        <MuiLink
                          component="button"
                          className="impres-supplier-part-link"
                          onClick={() =>
                            onOpenSupplierPart(
                              supplier.supplierName as SidebarSupplier,
                              supplier.supplierPartNumber ?? '',
                              item.mfgPN,
                            )
                          }
                        >
                          {supplier.supplierPartNumber}
                        </MuiLink>
                      ) : (
                        text(supplier.supplierPartNumber)
                      )}
                    </Typography>
                  </div>
                  <div className="impres-supplier-row-right">
                    <Typography color="text.secondary" className="impres-supplier-price">
                      {currency(supplier.unitPrice)}
                    </Typography>
                    <div className="impres-qty-stepper">
                      <IconButton
                        size="small"
                        disabled={qty <= 0}
                        onClick={() => setQty(item, supplier.supplierName, Math.max(0, qty - 1))}
                        aria-label={`Decrease ${supplier.supplierName} quantity`}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <TextField
                        size="small"
                        value={qty}
                        onChange={e => {
                          const parsed = parseInt(e.target.value, 10)
                          setQty(item, supplier.supplierName, isNaN(parsed) ? 0 : Math.max(0, parsed))
                        }}
                        slotProps={{ htmlInput: { inputMode: 'numeric', style: { textAlign: 'center' } } }}
                        className="impres-qty-input"
                      />
                      <IconButton
                        size="small"
                        onClick={() => setQty(item, supplier.supplierName, qty + 1)}
                        aria-label={`Increase ${supplier.supplierName} quantity`}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </div>
                    <Typography variant="h6" className="impres-supplier-total">
                      {qty > 0 && supplier.unitPrice !== null ? currency(supplier.unitPrice * qty) : dash}
                    </Typography>
                  </div>
                </div>
                {index < item.suppliers.length - 1 && <Divider />}
              </div>
            )
          })}
        </>
      )}
    </Card>
  )
})

export default function ImpresCatalog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [term, setTerm] = useState(() => searchParams.get('search') ?? '')
  const [filterSelection, setFilterSelection] = useState(EMPTY_FILTER_SELECTION)
  const { requestedTerm, results, loading, errorMessage, search, cancel, dismissError } = useImpresCatalogSearch()
  const { cart, setQty, itemCount, openDrawer } = useImpresCart()
  const {
    lookup: supplierPartLookup,
    openPart: openSupplierPart,
    closePart: closeSupplierPart,
  } = useSupplierPartLookup()

  useEffect(() => {
    document.title = 'IMPRES Catalog | I-Quote'
  }, [])

  useEffect(() => {
    const savedTerm = searchParams.get('search')
    if (savedTerm && savedTerm.trim().length >= 2) search(savedTerm)
  }, [])

  const hasSearched = results !== null

  const clearFilters = () => setFilterSelection(EMPTY_FILTER_SELECTION)

  const handleSearch = () => {
    if (term.trim().length < 2) return
    clearFilters()
    setSearchParams({ search: term.trim() }, { replace: true })
    search(term)
  }

  const filteredResults = useMemo(
    () => (results ?? []).filter(item => itemMatchesFilters(item, filterSelection)),
    [results, filterSelection],
  )

  return (
    <div className="impres-page">
      <div className="impres-toolbar">
        <Typography variant="h6" className="impres-search-label">
          Search by Part #, manufacturer or description
        </Typography>
        <div className="impres-search-row">
          <TextField
            fullWidth
            size="small"
            placeholder="Type a part number, manufacturer, or description"
            value={term}
            onChange={e => setTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSearch()
              else if (e.key === 'Escape' && loading === 'loading') cancel()
            }}
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            disabled={term.trim().length < 2}
            loading={loading === 'loading'}
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>
      </div>

      <div className="impres-body">
        <ImpresCatalogFilters
          results={results}
          selection={filterSelection}
          onChange={setFilterSelection}
          onClearAll={clearFilters}
        />
        <div className="impres-body-main">
          {!hasSearched ? (
            <div className="impres-empty-state">
              <img src={productSearchEmptyState} alt="" className="impres-empty-state-icon" />
              <Typography>Search results will display here</Typography>
            </div>
          ) : results.length === 0 ? (
            <div className="impres-empty-state">
              <img src={productSearchEmptyState} alt="" className="impres-empty-state-icon" />
              <Typography>No results found for {requestedTerm}</Typography>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="impres-empty-state">
              <img src={productSearchEmptyState} alt="" className="impres-empty-state-icon" />
              <Typography>No results match your filters</Typography>
              <Button onClick={clearFilters}>Clear filters</Button>
            </div>
          ) : (
            <div className="impres-results">
              {filteredResults.map(item => (
                <ImpresPartCard
                  key={item.id}
                  item={item}
                  cartForItem={cart[item.id]}
                  setQty={setQty}
                  onOpenSupplierPart={openSupplierPart}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Snackbar autoHideDuration={5000} open={loading === 'error'} onClose={dismissError} message={errorMessage} />

      <SupplierPartDrawer
        open={supplierPartLookup?.open ?? false}
        onClose={closeSupplierPart}
        title={supplierPartLookup?.partNumber ?? ''}
        supplierLabel={supplierPartLookup?.supplier ?? ''}
        status={supplierPartLookup?.status ?? 'loading'}
        fields={supplierPartLookup?.fields ?? null}
      />

      <Fab variant="extended" color="primary" size="large" disabled={itemCount === 0} onClick={openDrawer}>
        Add to Cart
      </Fab>
    </div>
  )
}
